import { memo, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FormSwitchField from 'components/ui/form/FormSwitchField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useFormSubmit from 'hooks/useFormSubmit';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';

const switchOptions = [
  { value: 1, checked: true },
  { value: 0, checked: false },
];

const validationSchema = yup.object({
  assignee_type: yup.string().required(),
  assignee_user_id: yup.number().when('assignee_type', {
    is: 'fixed',
    then: (schema) => schema.required('tasks.staffTask.debtTask.assigneeUser'),
  }),
});

const DebtTaskSettingsModal = ({ open, setOpen, refetchFn }) => {
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const assigneeTypeOptions = [
    { id: 'responsible', name: t('tasks.staffTask.debtTask.assigneeResponsible') },
    { id: 'operator', name: t('tasks.staffTask.debtTask.assigneeOperator') },
    { id: 'fixed', name: t('tasks.staffTask.debtTask.assigneeFixed') },
  ];

  const formik = useFormik({
    initialValues: {
      enabled: 0,
      assignee_type: 'responsible',
      assignee_user_id: '',
      fallback_user_id: '',
    },
    validationSchema,
    onSubmit: (values) => {
      submit(
        { type: 'post', contentType: 'simple' },
        {
          enabled: values.enabled ? 1 : 0,
          assignee_type: values.assignee_type,
          assignee_user_id:
            values.assignee_type === 'fixed' ? values.assignee_user_id : '',
          fallback_user_id: values.fallback_user_id || '',
        },
        '/admin/task-staff/debt-settings',
        { title: t('tasks.staffTask.debtTask.saved') },
        null,
        true,
        handleClose
      );
    },
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axiosPrivate
      .get('/admin/task-staff/debt-settings')
      .then((response) => {
        const data = response?.data?.data;
        if (data) {
          formik.setValues({
            enabled: data.enabled ? 1 : 0,
            assignee_type: data.assignee_type || 'responsible',
            assignee_user_id: data.assignee_user_id || '',
            fallback_user_id: data.fallback_user_id || '',
          });
        }
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleRunNow = async () => {
    try {
      setRunning(true);
      const response = await axiosPrivate.post('/admin/task-staff/debt-run');
      const count = response?.data?.data?.created ?? 0;
      sendNotification({
        msg: t('tasks.staffTask.debtTask.runResult', { count }),
        variant: 'success',
      });
      refetchFn?.();
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || error?.message,
        variant: 'error',
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
      disableEscapeKeyDown
    >
      <DialogTitle className='!bg-gray-100 !mb-2 text-center'>
        {t('tasks.staffTask.debtTask.title')}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <div className='flex justify-center py-8'>
            <CircularProgress size={30} />
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit} className='pt-2'>
            <p className='text-sm text-gray-500 mb-3'>
              {t('tasks.staffTask.debtTask.description')}
            </p>

            <Grid container spacing={2} rowSpacing={1} columns={{ xs: 12 }}>
              <Grid item xs={12}>
                <FormSwitchField
                  delay={0}
                  label={t('tasks.staffTask.debtTask.enabled')}
                  fieldName='enabled'
                  formik={formik}
                  options={switchOptions}
                />
              </Grid>

              <Grid item xs={12}>
                <FormSimpleSelectField
                  delay={0.1}
                  fieldName='assignee_type'
                  label={t('tasks.staffTask.debtTask.assigneeType')}
                  formik={formik}
                  options={assigneeTypeOptions}
                  itemValue='id'
                  itemLabel='name'
                />
              </Grid>

              {formik.values.assignee_type === 'fixed' && (
                <Grid item xs={12}>
                  <FormSelectField
                    delay={0.2}
                    fieldName='assignee_user_id'
                    label={t('tasks.staffTask.debtTask.assigneeUser')}
                    formik={formik}
                    path='/dictionary/staffes'
                    isitemsizesmall
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <FormSelectField
                  delay={0.3}
                  fieldName='fallback_user_id'
                  label={t('tasks.staffTask.debtTask.fallbackUser')}
                  formik={formik}
                  path='/dictionary/staffes'
                  isitemsizesmall
                />
                <span className='text-xs text-gray-400'>
                  {t('tasks.staffTask.debtTask.fallbackHint')}
                </span>
              </Grid>

              <Grid item xs={12}>
                <div className='flex justify-between gap-2 pt-2'>
                  <Button
                    variant='base'
                    color='info'
                    disabled={running || !formik.values.enabled}
                    onClick={handleRunNow}
                  >
                    {running ? (
                      <CircularProgress size={18} />
                    ) : (
                      t('tasks.staffTask.debtTask.runNow')
                    )}
                  </Button>

                  <div className='flex gap-2'>
                    <Button
                      variant='base'
                      color='secondary'
                      onClick={handleClose}
                    >
                      {t('common.button.cancel')}
                    </Button>
                    <Button
                      variant='base'
                      color='success'
                      type='submit'
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={18} />
                      ) : (
                        t('common.button.save')
                      )}
                    </Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default memo(DebtTaskSettingsModal);
